import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatRupiah, formatTanggal, formatApiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import SuratDetailDialog from "@/components/SuratDetailDialog";
import { useAuth } from "@/context/AuthContext";
import { statusBadgeClass } from "@/lib/surat-status";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, FileDown, FileText, Filter, MessageCircle, MoreHorizontal, Pencil, Search, Trash2 } from "lucide-react";

export default function SuratPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ jenis: "all", status: "all", user_id: "all", start: "", end: "", q: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [editing, setEditing] = useState(null); // { id, nama_pemohon, nominal, nomor }
  const [editSaving, setEditSaving] = useState(false);
  const [detailFor, setDetailFor] = useState(null); // surat for SuratDetailDialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.jenis !== "all") params.jenis = filter.jenis;
      if (filter.status !== "all") params.status = filter.status;
      if (filter.user_id !== "all") params.user_id = filter.user_id;
      if (filter.start) params.start = filter.start;
      if (filter.end) params.end = filter.end;
      if (filter.q) params.q = filter.q;
      const { data } = await api.get("/surat", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter.jenis, filter.status, filter.user_id, filter.start, filter.end, filter.q]);

  useEffect(() => {
    load();
    if (isAdmin) {
      api
        .get("/users")
        .then((r) => setUsers(r.data))
        .catch((e) => {
          // Non-critical: user filter list. Log so it's traceable.
          console.warn("Failed to load user list for filter:", formatApiError(e));
        });
    }
  }, [load, isAdmin]);

  async function changeStatus(id, status) {
    try {
      const { data } = await api.patch(`/surat/${id}/status`, { status });
      setItems((prev) => prev.map((s) => (s.id === id ? data : s)));
      toast.success(`Status diubah menjadi ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteSurat(id, nomor) {
    try {
      await api.delete(`/surat/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      toast.success(`Surat ${nomor} dihapus`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openEdit(s) {
    setEditing({
      id: s.id,
      nomor: s.nomor,
      nama_pemohon: s.nama_pemohon,
      kategori: s.kategori || "Perorangan",
      nama_marketing: s.nama_marketing || "",
      nomor_wa: s.nomor_wa || "",
      nomor_wa_hidden: !!s.nomor_wa_hidden,
      nominal: String(s.nominal),
    });
  }

  async function saveEdit() {
    if (!editing) return;
    const body = {};
    if (editing.nama_pemohon?.trim()) body.nama_pemohon = editing.nama_pemohon.trim();
    if (editing.kategori) body.kategori = editing.kategori;
    if (editing.nama_marketing?.trim()) body.nama_marketing = editing.nama_marketing.trim();
    // nomor_wa: kirim hanya jika viewer tidak "hidden" (artinya milik sendiri/admin)
    if (!editing.nomor_wa_hidden) body.nomor_wa = editing.nomor_wa?.trim() || null;
    const nominalNum = Number(editing.nominal);
    if (!Number.isNaN(nominalNum) && nominalNum >= 0) body.nominal = nominalNum;
    if (!body.nama_pemohon || !body.nama_marketing || body.nominal === undefined) {
      return toast.error("Nama pemohon, nama marketing, dan nominal wajib diisi dengan benar");
    }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/surat/${editing.id}`, body);
      setItems((prev) => prev.map((s) => (s.id === data.id ? data : s)));
      toast.success(`Surat ${data.nomor} diperbarui`);
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  }

  function exportExcel() {
    const params = new URLSearchParams();
    if (filter.jenis !== "all") params.set("jenis", filter.jenis);
    if (filter.status !== "all") params.set("status", filter.status);
    if (filter.user_id !== "all") params.set("user_id", filter.user_id);
    if (filter.start) params.set("start", filter.start);
    if (filter.end) params.set("end", filter.end);
    const url = `${API}/export/excel?${params.toString()}`;
    // open in new tab with credentials
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const filtered = useMemo(() => {
    if (!filter.q) return items;
    const q = filter.q.toLowerCase();
    return items.filter(
      (s) =>
        s.nama_pemohon?.toLowerCase().includes(q) ||
        s.nomor?.toLowerCase().includes(q) ||
        s.user_username?.toLowerCase().includes(q),
    );
  }, [items, filter.q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paged = filtered.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    setPage(1);
  }, [filter.q, filter.jenis, filter.status, filter.user_id, filter.start, filter.end, pageSize]);

  return (
    <div className="space-y-6" data-testid="surat-page">
      <PageHeader
        testId="surat-header"
        overline="Rekap"
        title="Rekap Surat"
        description="Daftar seluruh surat SPH & Invoice yang diterbitkan di sistem. Anda dapat mengubah surat milik sendiri."
        icon={FileText}
        tone="brand"
        actions={
          isAdmin && (
            <Button onClick={exportExcel} className="bg-ink-900 text-white hover:bg-brand" data-testid="btn-export-excel">
              <Download className="mr-2 h-4 w-4" /> Export Excel
            </Button>
          )
        }
      />

      <Card className="p-5 border-border shadow-none">
        <div className="mb-3 flex items-center gap-2 text-sm text-ink-500">
          <Filter className="h-4 w-4" /> Filter
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2 space-y-1">
            <Label htmlFor="q">Cari</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-500" />
              <Input
                id="q"
                data-testid="filter-search"
                value={filter.q}
                onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                placeholder="Nomor / nama pemohon / user"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Jenis</Label>
            <Select value={filter.jenis} onValueChange={(v) => setFilter({ ...filter, jenis: v })}>
              <SelectTrigger data-testid="filter-jenis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="SPH">SPH</SelectItem>
                <SelectItem value="Invoice">Invoice</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="Pengajuan">Pengajuan</SelectItem>
                <SelectItem value="Selesai">Selesai</SelectItem>
                <SelectItem value="Batal">Batal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className="space-y-1">
              <Label>User</Label>
              <Select value={filter.user_id} onValueChange={(v) => setFilter({ ...filter, user_id: v })}>
                <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label>Dari</Label>
            <DatePicker
              value={filter.start}
              onChange={(v) => setFilter({ ...filter, start: v })}
              testId="filter-start"
              placeholder="Tanggal awal"
              allowClear
            />
          </div>
          <div className="space-y-1">
            <Label>Sampai</Label>
            <DatePicker
              value={filter.end}
              onChange={(v) => setFilter({ ...filter, end: v })}
              testId="filter-end"
              placeholder="Tanggal akhir"
              allowClear
            />
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden border-border shadow-none">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Nomor</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Pemohon</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Marketing</TableHead>
                <TableHead className="text-right">Nominal</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={10} className="py-8 text-center text-ink-500">Memuat…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={10} className="py-8 text-center text-ink-500">Tidak ada data.</TableCell></TableRow>
              )}
              {!loading && paged.map((s) => (
                <TableRow key={s.id} data-testid={`row-surat-${s.id}`}>
                  <TableCell className="whitespace-nowrap">{formatTanggal(s.tanggal)}</TableCell>
                  <TableCell className="font-mono text-sm">{s.nomor}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        s.jenis === "SPH"
                          ? "bg-sky-100 text-sky-800 hover:bg-sky-100"
                          : "bg-amber-100 text-amber-800 hover:bg-amber-100"
                      }
                    >
                      {s.jenis}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[240px]">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{s.nama_pemohon}</span>
                      {s.nomor_wa && !s.nomor_wa_hidden && (
                        <a
                          href={`https://wa.me/${s.nomor_wa.replace(/\D/g, "").replace(/^0/, "62")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                          title={`Chat via WhatsApp: ${s.nomor_wa}`}
                          data-testid={`wa-chat-${s.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MessageCircle className="h-3 w-3" />
                          <span>Chat</span>
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{s.kategori}</TableCell>
                  <TableCell className="text-ink-700">{s.nama_marketing || <span className="text-ink-500">—</span>}</TableCell>
                  <TableCell className="text-right font-mono">{formatRupiah(s.nominal)}</TableCell>
                  <TableCell className="text-ink-500">@{s.user_username}</TableCell>
                  <TableCell>
                    <Badge className={statusBadgeClass(s.status)}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  {(() => {
                    const ownedByMe = s.user_id === user?.id;
                    const canEdit = isAdmin || ownedByMe;
                    const canChangeStatus = isAdmin;
                    const canDelete = isAdmin;
                    if (!canEdit && !canChangeStatus && !canDelete) {
                      return <TableCell className="text-right text-ink-500">—</TableCell>;
                    }
                    return (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`menu-surat-${s.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canEdit && (
                              <DropdownMenuItem onClick={() => openEdit(s)} data-testid={`edit-surat-${s.id}`}>
                                <Pencil className="mr-2 h-4 w-4" /> Ubah nama / nominal
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <DropdownMenuItem onClick={() => setDetailFor(s)} data-testid={`detail-pdf-${s.id}`}>
                                <FileDown className="mr-2 h-4 w-4" /> Detail & Unduh PDF
                              </DropdownMenuItem>
                            )}
                            {canChangeStatus && (
                              <>
                                <DropdownMenuItem onClick={() => changeStatus(s.id, "Pengajuan")} data-testid={`set-pengajuan-${s.id}`}>
                                  Tandai Pengajuan
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => changeStatus(s.id, "Selesai")} data-testid={`set-selesai-${s.id}`}>
                                  Tandai Selesai
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => changeStatus(s.id, "Batal")} data-testid={`set-batal-${s.id}`}>
                                  Tandai Batal
                                </DropdownMenuItem>
                              </>
                            )}
                            {canDelete && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive focus:text-destructive" data-testid={`delete-surat-${s.id}`}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Hapus surat
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Hapus surat {s.nomor}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Surat dan riwayatnya akan dihapus permanen. Nomor urut yang sudah terbit
                                      <span className="font-mono"> {s.nomor}</span> tidak dipakai ulang oleh sistem.
                                      Tindakan ini tercatat di Audit Log.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Batal</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteSurat(s.id, s.nomor)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      data-testid={`confirm-delete-surat-${s.id}`}
                                    >
                                      Hapus
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    );
                  })()}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {!loading && filtered.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-border bg-secondary/30 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="pagination-bar">
            <div className="flex items-center gap-2 text-ink-500">
              <span>
                Menampilkan <span className="font-medium text-ink-900">{pageStart + 1}</span>–
                <span className="font-medium text-ink-900">{Math.min(pageStart + pageSize, filtered.length)}</span> dari{" "}
                <span className="font-medium text-ink-900">{filtered.length}</span> surat
              </span>
              <span className="hidden sm:inline">·</span>
              <div className="flex items-center gap-2">
                <Label htmlFor="page-size" className="text-xs text-ink-500">Baris per halaman</Label>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger id="page-size" data-testid="page-size-select" className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="page-prev"
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Sebelumnya
              </Button>
              <span className="px-2 font-mono text-xs text-ink-500" data-testid="page-indicator">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="page-next"
              >
                Berikutnya <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-surat-dialog">
          <DialogHeader>
            <DialogTitle>Ubah surat {editing?.nomor}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="rounded-md border border-dashed border-border bg-secondary/40 p-3 text-xs text-ink-500">
                Yang dapat diubah: nama pemohon, kategori, nama marketing, dan nominal.
                Nomor, tanggal, dan jenis surat tidak dapat diubah.
              </div>
              <div>
                <Label>Nama Pemohon / Badan Usaha</Label>
                <Input
                  value={editing.nama_pemohon}
                  onChange={(e) => setEditing({ ...editing, nama_pemohon: e.target.value })}
                  data-testid="edit-nama-pemohon"
                  autoFocus
                />
              </div>
              <div>
                <Label>Kategori Pemohon</Label>
                <Select
                  value={editing.kategori}
                  onValueChange={(v) => setEditing({ ...editing, kategori: v })}
                >
                  <SelectTrigger data-testid="edit-kategori"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Perorangan">Perorangan</SelectItem>
                    <SelectItem value="Badan Usaha">Badan Usaha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nama Marketing</Label>
                <Input
                  value={editing.nama_marketing}
                  onChange={(e) => setEditing({ ...editing, nama_marketing: e.target.value })}
                  data-testid="edit-nama-marketing"
                  placeholder="Contoh: Andi Pratama"
                />
              </div>
              {!editing.nomor_wa_hidden && (
                <div>
                  <Label>Nomor WhatsApp Pemohon <span className="text-ink-400">(opsional)</span></Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={editing.nomor_wa}
                    onChange={(e) => setEditing({ ...editing, nomor_wa: e.target.value })}
                    data-testid="edit-nomor-wa"
                    placeholder="Contoh: 08123456789"
                  />
                  <p className="mt-1 text-xs text-ink-500">Hanya Anda &amp; admin yang bisa melihat/ubah nomor ini.</p>
                </div>
              )}
              <div>
                <Label>Nominal (Rp)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={editing.nominal}
                  onChange={(e) => setEditing({ ...editing, nominal: e.target.value })}
                  data-testid="edit-nominal"
                />
                {editing.nominal && !Number.isNaN(Number(editing.nominal)) && (
                  <p className="mt-1 text-xs text-ink-500">{formatRupiah(Number(editing.nominal))}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Batal</Button>
            <Button onClick={saveEdit} disabled={editSaving} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-save-edit-surat">
              <Pencil className="mr-2 h-4 w-4" /> {editSaving ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SuratDetailDialog
        open={!!detailFor}
        onOpenChange={(v) => !v && setDetailFor(null)}
        surat={detailFor}
        onSaved={load}
      />
    </div>
  );
}
